import React, { FC, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';

import { Loading } from '@/components/Loading';
import { usePaymentFlags } from '@/feature/devsettings/hooks/use-payment-flags-store';
import { useFantasyMoneyFlags } from '@/hooks/payments/use-fantasy-money-flags';
import { useFantasyWalletFundingFlags } from '@/hooks/payments/use-fantasy-wallet-funding-flags';
import { usePaymentMethodEnabled } from '@/hooks/payments/use-payment-method-enabled';
import { usePaymentVisibility } from '@/hooks/payments/use-payment-visibility';
import { isTrustlyCompletedSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { common } from '@/styles/styles';
import { isAndroid, isWeb } from '@/utils/constants-platform-specific';

import { PaymentMethodTypes, paymentMethodOrder } from '../const';
import {
    PaymentMethod,
    PreferredPayment,
    hasSavedNuveiMethodsSelector,
    useDepositPaymentMethods,
} from '../hooks/use-deposit-payment-methods';
import { AddPaymentMethodBankTransfers } from './AddPaymentMethodBankTransfers';
import { AddPaymentMethodDebitCard } from './AddPaymentMethodDebitCard';
import { AddPaymentMethodFantasyMoney } from './AddPaymentMethodFantasyMoney';
import { AddPaymentMethodPayPal } from './AddPaymentMethodPayPal';
import { AddPaymentMethodSocialWallet } from './AddPaymentMethodSocialWallet';
import { AddPaymentMethodTrustly } from './AddPaymentMethodTrustly';
import { AddPaymentMethodVenmo } from './AddPaymentMethodVenmo';
import { PaymentMethodItem } from './PaymentMethodItem';

const mockNuveiPaymentMethod: PaymentMethod = {
    type: PaymentMethodTypes.PaysafeMazooma,
    value: 'paysafe_mazooma',
    name: 'txt.mazooma',
    supplier: 'paysafe',
    iframe: true,
    min: 0.01,
    max: 100000,
    logos: ['https://via.placeholder.com/150'],
    methods: {
        bank_transfer: {
            name: 'Bank Transfer',
            logos: ['https://via.placeholder.com/150'],
            position: 1,
        },
    },
    accountId: 'mock-nuvei-account-id',
    order: 3,
};

type PaymentMethodsProps = {
    handleSelect: (method: PaymentMethod, preferredMethod?: PreferredPayment) => void;
    selectedAmount: string;
    selectedPaymentMethod?: PaymentMethod;
    selectedPreferredMethod?: PreferredPayment;
};

/**
 * Show online bank transfers first, as it costs less to use over PayPal
 * TODO: Remove this order logic when we have https://pitchtech.atlassian.net/browse/CSP-470 done by BE
 *
 * @param PaymentMethod[] payments - The payment methods to order
 *
 * @returns PaymentMethod[] - The ordered payment methods
 */
const orderPaymentMethods = (payments: PaymentMethod[]) => {
    return payments.sort((a, b) => paymentMethodOrder.indexOf(a.type) - paymentMethodOrder.indexOf(b.type));
};

export const PaymentMethods: FC<PaymentMethodsProps> = ({
    selectedAmount,
    selectedPaymentMethod,
    selectedPreferredMethod,
    handleSelect,
}) => {
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const { flags } = usePaymentFlags();
    const { data: depositPaymentMethods, isLoading } = useDepositPaymentMethods();
    const { data: isTrustlyCompleted } = useAuthUserConfig({ select: isTrustlyCompletedSelector });

    const {
        isDebugMode,
        enableNuvei,
        enablePayByBank,
        enableVenmo,
        enableInstantBankTransfers,
        enableDebitCard,
        isAddDebitCardAndDepositEnabled,
        isOnlyTrustlyAndSavedDebitOnAndroid,
        enableTrustly,
        enableTrustlyDepositForManualKycUsers,
        enablePayPal,
    } = usePaymentMethodEnabled();
    const { showDepositIbt, showPayByBankWeb } = usePaymentVisibility();
    const { isFantasyMoneyPurchaseEnabled } = useFantasyMoneyFlags();
    const { isFantasyWalletFundingEnabled } = useFantasyWalletFundingFlags();

    const hasSavedNuveiMethods = isDebugMode
        ? flags.hasSavedNuveiMethods.enabled
        : hasSavedNuveiMethodsSelector(depositPaymentMethods);

    useEffect(() => {
        if (!depositPaymentMethods?.data) {
            return;
        }

        const isDebugModeWithSavedMethods = isDebugMode && hasSavedNuveiMethods;

        // Filter payment methods based on debug mode
        let methodsToFilter = isDebugModeWithSavedMethods
            ? // In debug mode with saved methods, only keep PayPal payment method
              depositPaymentMethods.data.filter(method => method.type === PaymentMethodTypes.PaysafePaypal)
            : // In normal mode, keep all payment methods
              depositPaymentMethods.data;

        // Include mock Nuvei method if in debug mode with saved methods
        if (isDebugModeWithSavedMethods) {
            methodsToFilter = [...methodsToFilter, mockNuveiPaymentMethod];
        }

        const filteredMethods = methodsToFilter.filter(method => {
            switch (method.type) {
                case PaymentMethodTypes.Trustly:
                    return enableTrustly;
                case PaymentMethodTypes.PaysafePayByBank:
                    if (isWeb) {
                        return showPayByBankWeb;
                    }
                    return enablePayByBank;
                case PaymentMethodTypes.PaysafeMazooma:
                    const showMazooma = enableInstantBankTransfers && (enableNuvei || hasSavedNuveiMethods);
                    return showMazooma;
                case PaymentMethodTypes.Paysafe:
                case PaymentMethodTypes.Paysafe3ds:
                    return enableDebitCard;
                case PaymentMethodTypes.PaysafePaypal:
                    return isOnlyTrustlyAndSavedDebitOnAndroid ? false : enablePayPal;
                case PaymentMethodTypes.PaysafeVenmo:
                    if (isWeb) {
                        return null;
                    }
                    return isOnlyTrustlyAndSavedDebitOnAndroid ? false : enableVenmo;
                case PaymentMethodTypes.FantasyMoney:
                    return isFantasyMoneyPurchaseEnabled; // Only show Fantasy Money for sweeps product with balance
                default:
                    return false;
            }
        });

        const amountFilteredMethods = filteredMethods.filter(method => {
            const amount = parseFloat(selectedAmount);
            const isIncluded = amount >= method.min;
            return isIncluded;
        });

        const orderedMethods = orderPaymentMethods(amountFilteredMethods);

        setPaymentMethods(orderedMethods);
    }, [
        isDebugMode,
        depositPaymentMethods?.data,
        enableNuvei,
        hasSavedNuveiMethods,
        enableInstantBankTransfers,
        enableDebitCard,
        showDepositIbt,
        showPayByBankWeb,
        selectedAmount,
        enablePayByBank,
        enableVenmo,
        enableTrustly,
        isFantasyMoneyPurchaseEnabled,
        enablePayPal,
        isOnlyTrustlyAndSavedDebitOnAndroid,
    ]);

    const paypalPaymentMethod = useMemo(() => {
        return paymentMethods.find(payment => payment.type === PaymentMethodTypes.PaysafePaypal);
    }, [paymentMethods]);

    const isPayPalPreferredMethod = useMemo(() => {
        return depositPaymentMethods?.preferred_payments?.some(
            payment => payment.type === PaymentMethodTypes.PaysafePaypal
        );
    }, [depositPaymentMethods]);

    const isVenmoPreferredMethod = useMemo(() => {
        return depositPaymentMethods?.preferred_payments?.some(
            payment => payment.type === PaymentMethodTypes.PaysafeVenmo
        );
    }, [depositPaymentMethods]);

    // If a 3DS payment method is available, it will be used preferentially.
    const paysafeDebit =
        paymentMethods.find(payment => payment.type === PaymentMethodTypes.Paysafe3ds) ||
        paymentMethods.find(payment => payment.type === PaymentMethodTypes.Paysafe);

    // Determine whether to show Nuvei (Mazooma) payment methods
    const shouldShowNuveiMethod = (method: PaymentMethod): boolean => {
        if (isWeb) {
            // Web-specific logic
            // Show the option if nuvei is enabled and not hidden by showDepositIbt
            return enableNuvei && showDepositIbt;
        }
        // Always show saved Nuvei methods for existing users, regardless of other flags
        if (method.type === PaymentMethodTypes.PaysafeMazooma && hasSavedNuveiMethods) {
            return true;
        }

        // For new Nuvei methods, only show if both instant bank transfers are enabled and Nuvei is enabled
        if (method.type === PaymentMethodTypes.PaysafeMazooma) {
            return enableInstantBankTransfers && enableNuvei;
        }

        // For other method types, defer to the instant bank transfers flag
        return enableInstantBankTransfers;
    };

    // Determine whether to show the option to add new bank transfers
    const shouldShowAddBankTransfers = (): boolean => {
        if (isWeb) {
            // Web-specific logic
            // Show the option if nuvei is enabled and not hidden by showDepositIbt
            // Web does not use enableInstantBankTransfers flag because app logic is different
            return enableNuvei && showDepositIbt;
        } else {
            // Mobile-specific logic (iOS, Android)
            // On mobile, we don't use showDepositIbt flag
            return enableInstantBankTransfers && enableNuvei;
        }
    };

    const shouldShowAddVenmo = (): boolean => {
        if (isWeb || isVenmoPreferredMethod) {
            return false;
        }

        return enableVenmo ?? false;
    };

    const shouldShowAddTrustly = (): boolean => {
        // Short circuit if Trustly deposit is not enabled or if on web
        if (!enableTrustly || isWeb) {
            return false;
        }

        // Show Trustly deposit method if user completed KYC via Trustly, or if it's enabled for manual KYC users on dev flag or prismic.
        return isTrustlyCompleted || enableTrustlyDepositForManualKycUsers;
    };

    if (isLoading) {
        return (
            <View style={[common.paddingVertical]} testID="loadingIndicator">
                <Loading />
            </View>
        );
    }

    return (
        <>
            {paymentMethods.map(method => {
                return (
                    <View key={method.type} testID={method.type}>
                        <PaymentMethodItem
                            item={method}
                            preferredPayments={depositPaymentMethods?.preferred_payments ?? []}
                            isSelected={selectedPaymentMethod?.type === method.type}
                            selectedPreferredItem={selectedPreferredMethod}
                            handleSelect={handleSelect}
                            showNuvei={shouldShowNuveiMethod(method)}
                        />
                    </View>
                );
            })}

            {isFantasyMoneyPurchaseEnabled ? (
                <View testID="AddPaymentMethodFantasyMoney">
                    <AddPaymentMethodFantasyMoney
                        handleSelect={handleSelect}
                        isSelected={selectedPaymentMethod?.type === PaymentMethodTypes.FantasyMoney}
                    />
                </View>
            ) : null}
            {isFantasyWalletFundingEnabled ? (
                <View testID="AddPaymentMethodSocialWallet">
                    <AddPaymentMethodSocialWallet
                        handleSelect={handleSelect}
                        isSelected={selectedPaymentMethod?.type === PaymentMethodTypes.SocialWallet}
                    />
                </View>
            ) : null}
            {isAddDebitCardAndDepositEnabled && paysafeDebit?.accountId ? (
                <View testID="AddPaymentMethodDebitCard">
                    <AddPaymentMethodDebitCard
                        paysafeAccountId={paysafeDebit.accountId}
                        canUse3ds={paysafeDebit?.type === PaymentMethodTypes.Paysafe3ds}
                    />
                </View>
            ) : null}
            {shouldShowAddTrustly() ? (
                <View testID="AddTrustlyDepositMethod">
                    <AddPaymentMethodTrustly />
                </View>
            ) : null}
            {shouldShowAddVenmo() ? (
                // This zIndex -1 is necessary for Android fix, otherwise it will prevent
                // the other methods to be selected
                <View style={isAndroid ? common.zIndexNegative1 : {}} testID="AddPaymentMethodVenmo">
                    <AddPaymentMethodVenmo />
                </View>
            ) : null}
            {!isPayPalPreferredMethod && paypalPaymentMethod && enablePayPal ? (
                <View testID="AddPaymentMethodPayPal">
                    <AddPaymentMethodPayPal paymentMethod={paypalPaymentMethod} />
                </View>
            ) : null}
            {shouldShowAddBankTransfers() ? (
                <View testID="AddPaymentMethodBankTransfers">
                    <AddPaymentMethodBankTransfers />
                </View>
            ) : null}
        </>
    );
};
